import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

export type Theme = 'midnight' | 'foundry' | 'daylight' | 'cyber'

interface ThemeContextType {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

interface ThemeProviderProps {
  children: ReactNode
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>('midnight')

  useEffect(() => {
    // Load theme from localStorage on mount
    const savedTheme = localStorage.getItem('nyos-theme') as Theme
    if (savedTheme && ['midnight', 'foundry', 'daylight', 'cyber'].includes(savedTheme)) {
      setThemeState(savedTheme)
    }
  }, [])

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme)
    localStorage.setItem('nyos-theme', newTheme)
    
    // Apply theme to document root
    document.documentElement.setAttribute('data-theme', newTheme)
  }

  useEffect(() => {
    // Apply initial theme to document root
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme])

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}
